// ui/data/liveCache.js — socket events written straight into the query cache.
//
// Pages used to patch their own state from `botUpdate:<id>` and lose it on
// unmount. These hooks write into the shared cache instead, so a cached bot
// list / history / Portfolio feed stays current while its page is closed and
// renders current on the way back. AppShell mounts both on the shell's socket
// (useRiskWatch); useAutoTrader mounts useBotRoomSync on its own socket only
// when it renders outside the shell. One writer per socket, never two.
//
//   useBotRoomSync   the AutoTrader contract, byte for byte: `join` (userId) +
//                    `joinBotRoom` per bot on every (re)connect, one
//                    `botUpdate:<id>` listener per bot in the cached list →
//                    applyBotSocketUpdate on ['bots','merged'] and the socket's
//                    history entries merged into ['botHistory', id] when that
//                    history is cached. `leaveBotRoom` only on a socket the
//                    hook owns (never on the shell's).
//   useBotFeedSync   Portfolio's feed (was pages/Portfolio/useBotFeed state):
//                    `onAny` `botUpdate:*` → ['live','botFeed'] =
//                    { stats, live, history } per bot id.
import { useEffect, useMemo, useRef } from 'react';
import {
  socketStatsOf, socketHistoryEntries, applyBotSocketUpdate, mergeHistoryEntries,
} from '../../containers/Trader_Form/botLive';
import { parseStats, parseSocketHistory } from '../../pages/Portfolio/portfolioModel';
import { futuresBlockFromSocket } from '../../components/Bot/botStatus';
import { useAppQueryClient } from './queryClient';
import { qk, useAppQuery, fetchBotsMerged } from './queries';

export const FEED_HISTORY_MAX = 50;
export const EMPTY_FEED = Object.freeze({ stats: {}, live: {}, history: {} });

/** Apply one `botUpdate:<id>` payload to the cached AutoTrader list + that bot's history. */
export function writeBotUpdate(client, botId, data) {
  const id = parseInt((data && (data.bot_id || data.id)) || botId, 10);
  if (!id) return;
  const payload = data || {};
  const stats = socketStatsOf(payload);
  const entries = socketHistoryEntries(payload);
  if (entries.length) {
    client.setQueryData(qk.botHistory(id), (prev) => (prev ? { ...prev, entries: mergeHistoryEntries(prev.entries || [], entries) } : prev));
  }
  client.setQueryData(qk.botsMerged, (prev) => (Array.isArray(prev) ? prev.map((b) => (b.id !== id ? b : applyBotSocketUpdate(b, payload, stats))) : prev));
}

/** Apply one `botUpdate:*` payload to the Portfolio feed (useBotFeed's reducer). */
export function writeFeedUpdate(client, event, data) {
  if (typeof event !== 'string' || !event.startsWith('botUpdate:') || !data) return;
  const id = parseInt(data.bot_id || data.id || event.slice('botUpdate:'.length), 10);
  if (!id) return;
  const s = parseStats(data.stats || (data.botData && data.botData.stats));
  const patch = {};
  if (data.stop_trade_ASSET !== undefined) patch.stop_trade_ASSET = data.stop_trade_ASSET === true || data.stop_trade_ASSET === 'true';
  if (data.error_message !== undefined) patch.error_message = data.error_message || '';
  // Set or cleared from the event's full botStats hash; absent = no news.
  const block = futuresBlockFromSocket(data);
  if (block !== undefined) patch.futures_block = block;
  const entries = parseSocketHistory(data.history);
  if (!s && !Object.keys(patch).length && !entries.length) return;
  client.setQueryData(qk.botFeed, (prev) => {
    const cur = prev || EMPTY_FEED;
    return {
      stats: s ? { ...cur.stats, [id]: s } : cur.stats,
      live: Object.keys(patch).length ? { ...cur.live, [id]: { ...(cur.live[id] || {}), ...patch } } : cur.live,
      // De-duplicated: the snapshot a (re)join sends repeats entries already held.
      history: entries.length ? { ...cur.history, [id]: mergeFeedHistory(cur.history[id] || [], entries) } : cur.history,
    };
  });
}

/** One feed history entry's identity: every join re-sends the recent snapshot. */
const feedEntryKey = (e) => {
  let body;
  try { body = JSON.stringify(e && e.buy_sell_history); } catch (_) { body = String(e && e.buy_sell_history); }
  return `${e && e.date}|${body}`;
};

/** Append only entries not already held, keep the newest FEED_HISTORY_MAX. */
export function mergeFeedHistory(prev = [], incoming = []) {
  const seen = new Set(prev.map(feedEntryKey));
  const added = [];
  incoming.forEach((e) => {
    const k = feedEntryKey(e);
    if (seen.has(k)) return;
    seen.add(k);
    added.push(e);
  });
  return added.length ? [...prev, ...added].slice(-FEED_HISTORY_MAX) : prev;
}

const idsKeyOf = (rows) => (Array.isArray(rows)
  ? rows.map((b) => Number(b.id)).filter(Number.isFinite).sort((a, b) => a - b).join(',')
  : '');

/**
 * Keep ['bots','merged'] (and cached histories) current from the socket.
 * `shared`: the socket is the shell's — useRiskWatch already emits `join` on
 * connect, so no `join` here; `joinBotRoom` only for ids not joined yet on
 * this connection (each is an ownership check server-side); cleanup only
 * removes our listeners (no leaveBotRoom). Own socket: the AutoTrader
 * contract as before (join + joinBotRoom per bot, leaveBotRoom on cleanup).
 */
export function useBotRoomSync(socket, { shared = false } = {}) {
  const client = useAppQueryClient();
  // Observe the cached list without ever fetching it here.
  const { data: botIdsKey = '' } = useAppQuery({
    queryKey: qk.botsMerged,
    queryFn: () => fetchBotsMerged(client),
    enabled: false,
    select: idsKeyOf,
  });
  const listeners = useRef(new Map());
  // Bot rooms joined on the current connection (reset on every (re)connect
  // and when the socket itself changes).
  const joined = useRef({ socket: null, ids: new Set() });

  useEffect(() => {
    if (!socket) return undefined;
    const ids = botIdsKey ? botIdsKey.split(',').map(Number) : [];
    const userId = parseInt(localStorage.getItem('userId'), 10);

    const register = (botId) => {
      const channel = `botUpdate:${botId}`;
      if (listeners.current.has(channel)) return;
      const listener = (data) => writeBotUpdate(client, botId, data);
      listeners.current.set(channel, listener);
      socket.on(channel, listener);
    };
    const unregister = (botId) => {
      const channel = `botUpdate:${botId}`;
      const l = listeners.current.get(channel);
      if (l) { socket.off(channel, l); listeners.current.delete(channel); }
    };
    if (joined.current.socket !== socket) joined.current = { socket, ids: new Set() };
    const joinAll = () => {
      if (!shared && userId) socket.emit('join', userId);
      ids.forEach((id) => {
        if (!joined.current.ids.has(id)) {
          socket.emit('joinBotRoom', id);
          joined.current.ids.add(id);
        }
        register(id);
      });
    };
    // A reconnect loses every room: join them all again.
    const onConnect = () => { joined.current.ids = new Set(); joinAll(); };

    socket.on('connect', onConnect);
    if (socket.connected) joinAll();
    return () => {
      ids.forEach((id) => {
        if (!shared) { socket.emit('leaveBotRoom', id); joined.current.ids.delete(id); }
        unregister(id);
      });
      socket.off('connect', onConnect);
    };
  }, [botIdsKey, socket, shared, client]);
}

/** Keep the Portfolio feed (['live','botFeed']) current from the shell socket. */
export function useBotFeedSync(socket) {
  const client = useAppQueryClient();
  // Observed (never fetched) so the feed is not garbage-collected while the
  // Portfolio page is closed. Nothing here re-renders on a write.
  useAppQuery({ queryKey: qk.botFeed, queryFn: () => EMPTY_FEED, enabled: false, select: () => true });
  useEffect(() => {
    if (!socket || typeof socket.onAny !== 'function') return undefined;
    const onAny = (event, data) => writeFeedUpdate(client, event, data);
    socket.onAny(onAny);
    return () => socket.offAny(onAny);
  }, [socket, client]);
}

/** Portfolio's view of the feed: { stats, live, history } (empty until the socket speaks). */
export function useBotFeedData() {
  const { data } = useAppQuery({ queryKey: qk.botFeed, queryFn: () => EMPTY_FEED, enabled: false });
  return useMemo(() => data || EMPTY_FEED, [data]);
}
